/**
 * Maps the notification API shapes (notification.dto.ts) to the UI model. Every assumption about
 * the backend contract lives here, so reconciling with the backend changes this file only.
 */
import type {
  NotificationCategory,
  NotificationCountResponseDto,
  NotificationDeepLinkDto,
  NotificationDto,
  NotificationListResponseDto,
  NotificationPushFrame,
  NotificationRecipientState,
  NotificationSettingsDto,
  NotificationStatus,
  NotificationTaskDto,
} from './notification.dto';
import { MISSED_CLOCK_IN_GRACE_DEFAULT, NOTIFICATION_CODES } from './notification.dto';

/** Where "Open" navigates inside the dashboard (which uses section state, not routes). */
export type NotificationTarget =
  | { kind: 'patient'; patientId: string; tab: string }
  | { kind: 'schedule'; view: 'Today' | 'Week' }
  | { kind: 'admin'; tab: string }
  | { kind: 'billing'; tab: string };

export interface NotificationItem {
  id: string;
  code: string;
  tier: string | null;
  category: string;
  subjectType: string | null;
  subjectId: string | null;
  patientId: string | null;
  patientName: string | null;
  status: NotificationStatus;
  state: NotificationRecipientState;
  message: string;
  createdAt: string;
  escalatedAt: string | null;
  resolvedAt: string | null;
  ackAt: string | null;
  clearedAt: string | null;
  target: NotificationTarget | null;
  task: NotificationTaskDto | null;
  offlineHint: boolean;
}

const CATEGORY_BY_CODE: Record<string, NotificationCategory> = {
  PA_EXPIRING: 'Authorization',
  PA_EXHAUSTED: 'Authorization',
  PA_DENIED: 'Authorization',
  PA_INITIAL_DUE: 'Authorization',
  UNSTAFFED_SHIFT: 'Scheduling',
  MISSED_CLOCK_IN: 'Scheduling',
  ABANDONED_VISIT: 'Visit',
  POC_UNSIGNED: 'Orders',
  RECERT_OVERDUE: 'Orders',
  DISCHARGE_SUMMARY_DUE: 'Discharge',
  PATIENT_HOSPITALIZED: 'Visit',
  PAYMENT_FAILED: 'Billing',
};

/** Patient chart tab (dashboard subsection label) per code when the API sends no deep link. */
const DEFAULT_PATIENT_TAB: Record<string, string> = {
  PA_EXPIRING: 'Prior Authorizations',
  PA_EXHAUSTED: 'Prior Authorizations',
  PA_DENIED: 'Prior Authorizations',
  PA_INITIAL_DUE: 'Prior Authorizations',
  ABANDONED_VISIT: 'Visit History',
  POC_UNSIGNED: 'POC Certifications',
  RECERT_OVERDUE: 'POC Certifications',
  DISCHARGE_SUMMARY_DUE: 'Overview',
  PATIENT_HOSPITALIZED: 'Overview',
};

/** Deep-link tab keys → chart subsection labels (exact labels pass through). */
const PATIENT_TAB_ALIASES: Record<string, string> = {
  overview: 'Overview',
  dashboard: 'Overview',
  discharge: 'Overview',
  authorizations: 'Prior Authorizations',
  'prior-authorizations': 'Prior Authorizations',
  pa: 'Prior Authorizations',
  poc: 'POC Certifications',
  'poc-certifications': 'POC Certifications',
  certifications: 'POC Certifications',
  orders: 'Orders',
  schedule: 'Schedule',
  visits: 'Visit History',
  'visit-history': 'Visit History',
  charting: 'Charting',
  insurance: 'Insurance and Coverage',
  documents: 'Documents',
};

const BILLING_TAB_ALIASES: Record<string, string> = {
  claims: 'Claims',
  evv: 'EVV Reconciliation',
  'evv-reconciliation': 'EVV Reconciliation',
  reports: 'Reports',
};

const patientTab = (tab: string | undefined, code: string): string => {
  if (!tab) return DEFAULT_PATIENT_TAB[code] || 'Overview';
  return PATIENT_TAB_ALIASES[tab.toLowerCase()] || tab;
};

const scheduleView = (view: string | undefined): 'Today' | 'Week' =>
  view && /week|coverage/i.test(view) ? 'Week' : 'Today';

/** Resolve the deep link (object or path) to a dashboard target; falls back to the code's default. */
export function resolveTarget(deepLink: NotificationDeepLinkDto | null | undefined, code: string, patientId: string | null): NotificationTarget | null {
  // Backend form { tab, path, id }: resolve the path; its tab hint wins for patient tabs
  if (deepLink && typeof deepLink === 'object' && !deepLink.section && typeof deepLink.path === 'string' && deepLink.path) {
    const viaPath = resolveTarget(deepLink.path, code, patientId);
    if (viaPath && viaPath.kind === 'patient' && deepLink.tab) return { ...viaPath, tab: patientTab(deepLink.tab, code) };
    if (viaPath) return viaPath;
  }
  if (deepLink && typeof deepLink === 'object') {
    switch (deepLink.section) {
      case 'patient': {
        const id = deepLink.patientId || patientId;
        return id ? { kind: 'patient', patientId: id, tab: patientTab(deepLink.tab ?? undefined, code) } : null;
      }
      case 'schedule':
        return { kind: 'schedule', view: scheduleView(deepLink.tab ?? undefined) };
      case 'admin':
        return { kind: 'admin', tab: deepLink.tab || 'organizationAccount' };
      case 'billing':
        return { kind: 'billing', tab: BILLING_TAB_ALIASES[(deepLink.tab || '').toLowerCase()] || deepLink.tab || 'Claims' };
      default:
        break;
    }
  }
  if (typeof deepLink === 'string' && deepLink.trim()) {
    const parts = deepLink.split(/[?#]/)[0].split('/').filter(Boolean);
    const [head, a, b] = parts;
    if (head === 'patients' && a) return { kind: 'patient', patientId: a, tab: patientTab(b, code) };
    if (head === 'schedule') return { kind: 'schedule', view: scheduleView(a) };
    if (head === 'admin') return { kind: 'admin', tab: a || 'organizationAccount' };
    if (head === 'billing') return { kind: 'billing', tab: BILLING_TAB_ALIASES[(a || '').toLowerCase()] || 'Claims' };
  }
  // No usable deep link: the code's default destination
  if (code === 'UNSTAFFED_SHIFT' || code === 'MISSED_CLOCK_IN') return { kind: 'schedule', view: 'Today' };
  if (code === 'PAYMENT_FAILED') return { kind: 'admin', tab: 'organizationAccount' };
  return patientId ? { kind: 'patient', patientId, tab: patientTab(undefined, code) } : null;
}

const num = (meta: NotificationDto['meta'], key: string): number | null => {
  const v = meta?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Short text from code, tier and counts only (REQ-NTF-08). The API's own message wins. */
export function notificationMessage(dto: Pick<NotificationDto, 'code' | 'tier' | 'meta' | 'message'>): string {
  if (dto.message && dto.message.trim()) return dto.message.trim();
  const tier = (dto.tier || '').toUpperCase();
  const meta = dto.meta;
  switch (dto.code) {
    case 'PA_EXPIRING': {
      if (tier === 'EXPIRED') return 'Prior authorization expired with no follow-on authorization';
      const days = num(meta, 'daysLeft');
      return days !== null ? `Prior authorization ends in ${plural(days, 'day')}, no follow-on authorization` : 'Prior authorization ends within 7 days, no follow-on authorization';
    }
    case 'PA_EXHAUSTED': return 'All approved units used, no follow-on authorization';
    case 'PA_DENIED': return 'Prior authorization denied, no later authorization';
    case 'PA_INITIAL_DUE': return 'Initial prior authorization is overdue';
    case 'UNSTAFFED_SHIFT': {
      const hours = num(meta, 'hoursUntilStart');
      if (hours !== null) return `Shift starting in ${plural(hours, 'hour')} has no clinician`;
      return tier.includes('12') ? 'Shift starting within 12 hours has no clinician' : 'Shift starting within 48 hours has no clinician';
    }
    case 'MISSED_CLOCK_IN': {
      const minutes = num(meta, 'minutesLate');
      return minutes !== null ? `No clock-in ${plural(minutes, 'minute')} after the shift began` : 'No clock-in after the shift began';
    }
    case 'ABANDONED_VISIT': return 'Visit in progress for more than 16 hours';
    case 'POC_UNSIGNED': {
      const days = num(meta, 'daysUnsigned');
      return days !== null ? `Plan of care unsigned for ${plural(days, 'day')}` : 'Plan of care unsigned for more than 14 days';
    }
    case 'RECERT_OVERDUE': return 'Certification period ended while still in care';
    case 'DISCHARGE_SUMMARY_DUE': return tier === 'OVERDUE' ? 'Discharge summary is overdue' : 'Discharge summary is due today';
    case 'PATIENT_HOSPITALIZED': return 'Patient placed on hold: hospitalized';
    case 'PAYMENT_FAILED': return 'Subscription payment failed; the account will become read-only';
    default: return 'Critical alert';
  }
}

const STATES: ReadonlySet<string> = new Set(['NEW', 'ACKNOWLEDGED', 'CLEARED']);

export function mapNotification(dto: NotificationDto): NotificationItem {
  const id = dto.id || dto.notificationId || '';
  const code = String(dto.code || '');
  const patientId = dto.patientId || null;
  return {
    id,
    code,
    tier: dto.tier ?? null,
    category: dto.category || CATEGORY_BY_CODE[code] || 'Alert',
    subjectType: dto.subjectType ?? null,
    subjectId: dto.subjectId ?? null,
    patientId,
    patientName: dto.patientName && dto.patientName.trim() ? dto.patientName.trim() : null,
    status: dto.status === 'RESOLVED' ? 'RESOLVED' : 'OPEN',
    state: dto.state && STATES.has(dto.state) ? dto.state : 'NEW',
    message: notificationMessage(dto),
    createdAt: dto.createdAt,
    escalatedAt: dto.escalatedAt ?? null,
    resolvedAt: dto.resolvedAt ?? null,
    ackAt: dto.ackAt ?? null,
    clearedAt: dto.clearedAt ?? null,
    target: resolveTarget(dto.deepLink, code, patientId),
    task: dto.task ?? null,
    offlineHint: Boolean(dto.offlineHint),
  };
}

/** List payload: `{ items }` or a bare array. Rows without an id are dropped. */
export function mapNotificationList(data: NotificationListResponseDto | NotificationDto[] | null | undefined): NotificationItem[] {
  const rows = Array.isArray(data) ? data : data?.items || [];
  return rows.map(mapNotification).filter((n) => n.id);
}

/** Single payload: the notification, or `{ notification }`. */
export function mapNotificationDetail(data: NotificationDto | { notification: NotificationDto } | null | undefined): NotificationItem | null {
  if (!data) return null;
  const dto = 'notification' in data ? data.notification : data;
  const item = mapNotification(dto);
  return item.id ? item : null;
}

export function mapCount(data: NotificationCountResponseDto | number | null | undefined): number {
  if (typeof data === 'number') return Math.max(0, data);
  const value = data?.count ?? data?.newCount;
  return typeof value === 'number' && value > 0 ? value : 0;
}

/** Parses a WebSocket message; null for anything that is not a notification.* frame (any type means "refresh"). */
export function parsePushFrame(raw: unknown): NotificationPushFrame | null {
  if (typeof raw !== 'string') return null;
  try {
    const value = JSON.parse(raw) as { type?: unknown; notificationId?: unknown; newCount?: unknown } | null;
    if (!value || typeof value.type !== 'string' || !/^notifications?./.test(value.type)) return null;
    return {
      type: 'notifications.changed',
      notificationId: typeof value.notificationId === 'string' ? value.notificationId : undefined,
      newCount: typeof value.newCount === 'number' ? value.newCount : undefined,
    };
  } catch {
    return null;
  }
}

/** UI model of the settings form: roles per code, in catalog order, every code present. */
export interface NotificationSettingsModel {
  roles: Record<string, string[]>;
  missedClockInGraceMinutes: number;
  availableRoles: Array<{ key: string; label: string }>;
  version?: number;
}

/** Default routed roles (requirements §2.3); used for codes the API omits. */
export const DEFAULT_ROUTED_ROLES: Record<string, string[]> = {
  PA_EXPIRING: ['coordinator', 'billing'],
  PA_EXHAUSTED: ['coordinator', 'billing'],
  PA_DENIED: ['coordinator', 'billing'],
  PA_INITIAL_DUE: ['coordinator'],
  UNSTAFFED_SHIFT: ['scheduler', 'coordinator'],
  MISSED_CLOCK_IN: ['scheduler', 'coordinator'],
  ABANDONED_VISIT: ['coordinator'],
  POC_UNSIGNED: ['coordinator'],
  RECERT_OVERDUE: ['coordinator'],
  // The summary author always receives DISCHARGE_SUMMARY_DUE (not a role setting)
  DISCHARGE_SUMMARY_DUE: ['coordinator'],
  // Clinicians are assignment-scoped, so only the patient's care team receives it
  PATIENT_HOSPITALIZED: ['coordinator', 'clinician'],
  PAYMENT_FAILED: ['owner', 'admin'],
};

const DEFAULT_AVAILABLE_ROLES: Array<{ key: string; label: string }> = [
  { key: 'owner', label: 'Owner' },
  { key: 'admin', label: 'Admin' },
  { key: 'clinicalmgr', label: 'Clinical manager' },
  { key: 'coordinator', label: 'Coordinator' },
  { key: 'scheduler', label: 'Scheduler' },
  { key: 'billing', label: 'Billing' },
  { key: 'qa', label: 'QA' },
  { key: 'clinician', label: 'Clinician (assigned patients only)' },
];

const roleLabel = (key: string) => DEFAULT_AVAILABLE_ROLES.find((r) => r.key === key)?.label
  || key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export function mapSettings(dto: NotificationSettingsDto | null | undefined): NotificationSettingsModel {
  const roles: Record<string, string[]> = {};
  for (const code of NOTIFICATION_CODES) roles[code] = [...(DEFAULT_ROUTED_ROLES[code] || [])];
  for (const t of dto?.triggers || []) if (t && t.code) roles[t.code] = Array.isArray(t.roles) ? [...t.roles] : [];
  const available = (dto?.availableRoles?.length ? dto.availableRoles : dto?.routableRoles?.length ? dto.routableRoles : DEFAULT_AVAILABLE_ROLES).map((r) =>
    typeof r === 'string' ? { key: r, label: roleLabel(r) } : { key: r.key, label: r.label || roleLabel(r.key) });
  // Any routed role the list lacks still shows (so it can be removed)
  for (const list of Object.values(roles)) for (const key of list) if (!available.some((r) => r.key === key)) available.push({ key, label: roleLabel(key) });
  const grace = dto?.missedClockInGraceMinutes;
  return {
    roles,
    missedClockInGraceMinutes: typeof grace === 'number' ? grace : MISSED_CLOCK_IN_GRACE_DEFAULT,
    availableRoles: available,
    version: dto?.version,
  };
}

export const CODE_LABELS: Record<string, string> = {
  PA_EXPIRING: 'Prior authorization expiring (≤7 days) or expired',
  PA_EXHAUSTED: 'Prior authorization units used up',
  PA_DENIED: 'Prior authorization denied',
  PA_INITIAL_DUE: 'Initial prior authorization overdue',
  UNSTAFFED_SHIFT: 'Unstaffed shift (within 48 hours)',
  MISSED_CLOCK_IN: 'Missed clock-in',
  ABANDONED_VISIT: 'Visit in progress over 16 hours',
  POC_UNSIGNED: 'Plan of care unsigned over 14 days',
  RECERT_OVERDUE: 'Recertification overdue',
  DISCHARGE_SUMMARY_DUE: 'Discharge summary due',
  PATIENT_HOSPITALIZED: 'Patient hospitalized',
  PAYMENT_FAILED: 'Subscription payment failed',
};
